import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { MdsConfig } from '@maersk-global/mds-config';

import type { ThemeContextState, ThemeVariant } from '../contexts/ThemeContext';
import ThemeContext from '../contexts/ThemeContext';

import '@maersk-global/fonts/maeu/fonts.css';
import '@maersk-global/mds-design-tokens/alianca/light/css/design-tokens-px.css';
import '@maersk-global/mds-foundations/css/foundations.css';

MdsConfig.iconsDynamicImportPath = import.meta.env.DEV
  ? '/node_modules/'
  : './';

interface ThemeProviderProps {
  children: React.ReactNode;
}

const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const [themeVariant, setThemeVariant] = useState(() => {
    // retrieve the theme variant from localStorage or default to 'light'
    const themeVariantFromStorage =
      localStorage.getItem('[mds-theme-switch]theme') ?? 'light';

    return (themeVariantFromStorage ?? '').replace(/"/g, '') as ThemeVariant;
  });

  const darkThemeElementRef = useRef<HTMLElement | null>(null);

  const toggleThemeVariant = useCallback(() => {
    setThemeVariant((previous) => (previous === 'light' ? 'dark' : 'light'));
  }, [setThemeVariant]);

  useLayoutEffect(() => {
    const loadDarkStyles = async () => {
      await import(
        '@maersk-global/mds-design-tokens/alianca/dark/css/design-tokens-px.css'
      );

      darkThemeElementRef.current =
        document.querySelector(
          'style[data-vite-dev-id*="dark/css/design-tokens"]'
        ) ??
        document.querySelector('link[href*="assets/dark-design-tokens-px"]');
    };

    // first time dark theme is applied, load the dark styles
    // subsequent times, just append the already loaded styles
    if (themeVariant === 'dark') {
      if (darkThemeElementRef.current) {
        document.head.appendChild(darkThemeElementRef.current);
      } else {
        loadDarkStyles();
      }
    } else {
      darkThemeElementRef.current?.remove();
    }
  }, [themeVariant]);

  const contextValue: ThemeContextState = useMemo(
    () => ({
      themeVariant,
      toggleThemeVariant,
    }),
    [themeVariant, toggleThemeVariant]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
